import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Plus, Download, PaperPlaneTilt, Receipt as ReceiptIcon, Trash, ArrowsClockwise, CheckCircle, Clock, Warning, PencilSimple, Link as LinkIcon, ArrowCounterClockwise, Share } from "@phosphor-icons/react";
import { api, formatUSD, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CreateInvoiceModal } from "@/components/CreateInvoiceModal";
import { RecordInvoicePaymentModal } from "@/components/RecordInvoicePaymentModal";
import { ForwardInvoiceModal } from "@/components/ForwardInvoiceModal";

const STATUS_META = {
  draft:   { label: "Draft",   color: "text-[#a3a3a3]",   border: "border-[#262626]",       icon: Clock },
  sent:    { label: "Sent",    color: "text-[#FFD43B]",   border: "border-[#FFD43B]/40",    icon: PaperPlaneTilt },
  partial: { label: "Partial", color: "text-[#FFA94D]",   border: "border-[#FFA94D]/40",    icon: Warning },
  paid:    { label: "Paid",    color: "text-[#28C878]",   border: "border-[#28C878]/40",    icon: CheckCircle },
  void:    { label: "Void",    color: "text-[#666]",      border: "border-[#262626]",       icon: Trash },
};

export default function AdminInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editInvoice, setEditInvoice] = useState(null);
  const [recordPayInvoice, setRecordPayInvoice] = useState(null);
  const [forwardInvoice, setForwardInvoice] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/invoices${statusFilter ? `?status=${statusFilter}` : ""}`);
      setInvoices(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load invoices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter]);

  const sendInvoice = async (id) => {
    try {
      await api.post(`/admin/invoices/${id}/send`);
      toast.success("Invoice sent");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
    }
  };

  const downloadPdf = async (id, number) => {
    try {
      const r = await api.get(`/admin/invoices/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const unmarkPaid = async (inv) => {
    if (!window.confirm(`Un-mark ${inv.invoice_number} as paid?\n\nThis will clear ALL recorded payments ($${Number(inv.paid_amount || 0).toFixed(2)}) and revert the invoice to "Sent". Use this if a payment was recorded in error.`)) return;
    try {
      await api.post(`/admin/invoices/${inv.id}/unmark-paid`);
      toast.success(`${inv.invoice_number} reverted to Sent`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to un-mark");
    }
  };

  const voidInvoice = async (id) => {
    if (!window.confirm("Void this invoice? This can't be undone.")) return;
    try {
      await api.delete(`/admin/invoices/${id}`);
      toast.success("Voided");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap" data-testid="admin-invoices-header">
        <div>
          <h1 className="font-display text-3xl uppercase">Invoices</h1>
          <div className="text-xs text-[#a3a3a3] mt-1">Custom invoices for one-off + recurring work.</div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="invoice-status-filter"
            className="bg-[#0a0a0a] border border-[#262626] px-3 py-2 text-xs uppercase tracking-wider focus:border-[#FF2D2D] outline-none"
          >
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="partial">Partial</option>
            <option value="paid">Paid</option>
            <option value="void">Void</option>
          </select>
          <button
            onClick={() => setCreateOpen(true)}
            data-testid="new-invoice-btn"
            className="btn-primary px-4 py-2 text-xs uppercase tracking-wider flex items-center gap-2"
          >
            <Plus size={14} weight="bold" /> New invoice
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-[#a3a3a3]">Loading…</div>
      ) : invoices.length === 0 ? (
        <div className="border border-[#262626] bg-[#0a0a0a] p-12 text-center" data-testid="invoices-empty">
          <ReceiptIcon size={32} className="text-[#FF2D2D] mx-auto mb-3" weight="fill" />
          <div className="font-display text-xl uppercase">No invoices yet</div>
          <div className="text-xs text-[#a3a3a3] mt-2 max-w-md mx-auto">
            Click <strong>New invoice</strong> to bill a customer for one-time work or set up a recurring charge.
          </div>
        </div>
      ) : (
        <div className="border border-[#262626] divide-y divide-[#262626] bg-[#0a0a0a]" data-testid="invoices-list">
          {invoices.map((inv) => {
            const meta = STATUS_META[inv.status] || STATUS_META.draft;
            const Icon = meta.icon;
            const balance = Math.max(0, Number(inv.total_usd) - Number(inv.paid_amount || 0));
            const hasRecurring = (inv.line_items || []).some((l) => l.recurring);
            return (
              <div key={inv.id} className="p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`invoice-row-${inv.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider border px-2 py-0.5 ${meta.color} ${meta.border}`}>
                      <Icon size={10} weight="fill" /> {meta.label}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider text-[#666] font-mono">{inv.invoice_number}</span>
                    {hasRecurring && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#FF2D2D] border border-[#FF2D2D]/40 px-2 py-0.5">
                        <ArrowsClockwise size={10} weight="bold" /> Recurring
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-white truncate">{inv.title}</div>
                  <div className="text-[11px] text-[#a3a3a3] mt-0.5">
                    {inv.client_name}{inv.client_company ? ` · ${inv.client_company}` : ""} · Due {inv.due_date?.slice(0,10)}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display text-xl tabular-nums">{formatUSD(inv.total_usd)}</div>
                  {balance > 0 && balance < inv.total_usd && (
                    <div className="text-[10px] text-[#FFA94D] tabular-nums">{formatUSD(balance)} due</div>
                  )}
                  {balance <= 0 && inv.status === "paid" && (
                    <div className="text-[10px] text-[#28C878]">PAID ✓</div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0 flex-wrap justify-end">
                  {inv.public_token && inv.status !== "draft" && inv.status !== "void" && (
                    <button
                      onClick={() => {
                        const url = `${window.location.origin}/pay/invoice/${inv.public_token}`;
                        navigator.clipboard.writeText(url).then(
                          () => toast.success("Guest-pay link copied"),
                          () => toast.error("Could not copy link"),
                        );
                      }}
                      data-testid={`copy-pay-link-${inv.id}`}
                      title="Copy guest-pay link (no login required)"
                      className="text-[10px] uppercase tracking-wider border border-[#262626] hover:border-[#28C878] hover:text-[#28C878] text-[#a3a3a3] px-2.5 py-1.5 transition-colors flex items-center gap-1"
                    >
                      <LinkIcon size={10} weight="bold" /> Copy link
                    </button>
                  )}
                  {inv.status !== "void" && (
                    <button onClick={() => setEditInvoice(inv)} data-testid={`edit-${inv.id}`} title="Edit invoice"
                      className="text-[10px] uppercase tracking-wider border border-[#262626] hover:border-[#FF2D2D] hover:text-white text-[#a3a3a3] px-2.5 py-1.5 transition-colors flex items-center gap-1">
                      <PencilSimple size={10} weight="bold" /> Edit
                    </button>
                  )}
                  {inv.status === "draft" && (
                    <button onClick={() => sendInvoice(inv.id)} data-testid={`send-${inv.id}`} className="text-[10px] uppercase tracking-wider border border-[#FF2D2D]/40 bg-[#FF2D2D]/5 hover:bg-[#FF2D2D]/10 hover:border-[#FF2D2D] text-white px-2.5 py-1.5 transition-colors flex items-center gap-1">
                      <PaperPlaneTilt size={10} weight="bold" /> Send
                    </button>
                  )}
                  {inv.status !== "draft" && inv.status !== "void" && balance > 0 && (
                    <button onClick={() => setRecordPayInvoice(inv)} data-testid={`record-${inv.id}`} className="text-[10px] uppercase tracking-wider border border-[#262626] hover:border-[#FF2D2D] hover:text-white text-[#a3a3a3] px-2.5 py-1.5 transition-colors">
                      Record payment
                    </button>
                  )}
                  {inv.status !== "draft" && inv.status !== "void" && (
                    <button onClick={() => setForwardInvoice(inv)} data-testid={`forward-${inv.id}`}
                      title="Forward this invoice to another email address"
                      className="text-[10px] uppercase tracking-wider border border-[#262626] hover:border-[#FF2D2D] hover:text-white text-[#a3a3a3] px-2.5 py-1.5 transition-colors flex items-center gap-1">
                      <Share size={10} weight="bold" /> Forward
                    </button>
                  )}
                  {(inv.status === "paid" || inv.status === "partial") && (
                    <button onClick={() => unmarkPaid(inv)} data-testid={`unmark-${inv.id}`}
                      title="Un-mark paid (clears all recorded payments and reverts to Sent)"
                      className="text-[10px] uppercase tracking-wider border border-[#FFA94D]/40 hover:border-[#FFA94D] hover:text-[#FFA94D] text-[#a3a3a3] px-2.5 py-1.5 transition-colors flex items-center gap-1">
                      <ArrowCounterClockwise size={10} weight="bold" /> Un-mark paid
                    </button>
                  )}
                  <button onClick={() => downloadPdf(inv.id, inv.invoice_number)} data-testid={`pdf-${inv.id}`} className="text-[10px] uppercase tracking-wider border border-[#262626] hover:border-[#FF2D2D] hover:text-white text-[#a3a3a3] px-2.5 py-1.5 transition-colors flex items-center gap-1">
                    <Download size={10} weight="bold" /> PDF
                  </button>
                  {inv.status !== "paid" && inv.status !== "void" && (
                    <button onClick={() => voidInvoice(inv.id)} title="Void" className="text-[10px] uppercase tracking-wider border border-[#262626] hover:border-[#FF2D2D] hover:text-[#FF2D2D] text-[#a3a3a3] px-2 py-1.5 transition-colors">
                      <Trash size={10} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <CreateInvoiceModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => { setCreateOpen(false); load(); }}
      />
      <CreateInvoiceModal
        open={!!editInvoice}
        invoice={editInvoice}
        onClose={() => setEditInvoice(null)}
        onCreated={() => { setEditInvoice(null); load(); }}
      />
      <ForwardInvoiceModal
        open={!!forwardInvoice}
        invoice={forwardInvoice}
        onClose={() => setForwardInvoice(null)}
        onSent={() => setForwardInvoice(null)}
      />
      <RecordInvoicePaymentModal
        open={!!recordPayInvoice}
        invoice={recordPayInvoice}
        onClose={() => setRecordPayInvoice(null)}
        onSaved={() => { setRecordPayInvoice(null); load(); }}
      />
    </AdminLayout>
  );
}
